import React, { useEffect, useRef, useState } from "react";
import AuthLayout from "../component/AuthLayout";
import { Input, Button, Form, Card, Typography, Select, Space, message } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { actions, Types } from "../state";
import useBlockLoginUser from "../hook/useBlockLoginUser";
import useFetchInfo from "../../../common/hook/useFetchInfo";
import { FetchStatus } from "../../../common/constant";
import banner from "./../../../common/images/loginBanner.png";

export default function Signup() {
  useBlockLoginUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchStatus, errorMessage } = useFetchInfo(Types.FetchEmail);
  const [form] = Form.useForm();
  const passwordRef = useRef(null);

  const defaultSelect = "naver.com";
  const [selectedOption, setSelectedOption] = useState(defaultSelect);
  const [selfSelect, setSelfSelect] = useState("");
  const [id, setId] = useState("");
  const [emailError, setEmailError] = useState("");

  const key = "mailSend";

  useEffect(() => {
    if (fetchStatus === FetchStatus.Success) {
      message.success({ content: "인증 메일이 발송되었습니다", key, duration: 2 });
    } else if (fetchStatus === FetchStatus.Request) {
      message.loading({ content: "처리중", key, duration: 0 });
    } else if (fetchStatus === FetchStatus.Fail) {
      message.destroy(key);
      if (errorMessage?.includes("닉네임")) {
        form.setFields([{ name: "nickname", errors: [errorMessage] }]);
      } else {
        setEmailError(errorMessage || "오류가 발생했습니다.");
      }
    }
  }, [fetchStatus, errorMessage, form]);

  const getEmail = () => {
    const domain = selectedOption !== "selfSelect" ? selectedOption : selfSelect;
    return `${id}@${domain}`;
  };

  const validateNickName = (_, value) => {
    const regEx = /^[0-9a-zA-Zㄱ-힣]{2,20}$/;
    if (!value) return Promise.reject(new Error("닉네임을 입력해주세요!"));
    if (!regEx.test(value)) return Promise.reject(new Error("한/영/숫자 2~20자 이내로 입력해주세요!"));
    return Promise.resolve();
  };

  const validatePwd = (_, value) => {
    const regex = /^(?=.*[a-zA-z])(?=.*[0-9])(?=.*[$`~!@$!%*#^?&\\(\\)\-_=+]).{8,16}$/;
    if (!value) return Promise.reject(new Error("비밀번호를 입력해주세요!"));
    if (!regex.test(value)) return Promise.reject(new Error("영문+숫자+특수문자 포함 8~16자로 입력해주세요!"));
    return Promise.resolve();
  };

  const validatePwdConfirm = (_, value) => {
    if (!value) return Promise.reject(new Error("비밀번호를 다시 입력해주세요!"));
    if (value !== passwordRef?.current?.input?.value) return Promise.reject(new Error("비밀번호가 일치하지 않습니다!"));
    return Promise.resolve();
  };

  const onFinish = (values) => {
    setEmailError("");
    dispatch(actions.fetchEmail({
      email: getEmail(),
      password: values.password,
      nickname: values.nickname,
    }));
  };

  return (
    <AuthLayout onFinish={onFinish} form={form}>
      <Card
        style={{ width: "20rem" }}
        cover={
          <img onClick={() => navigate("/")} alt="banner" src={banner} />
        }
        actions={[
          <Typography.Text onClick={() => navigate("/login")} style={{ fontWeight: "bold" }}>
            로그인
          </Typography.Text>,
        ]}
      >
        <Form.Item
          name="emailId"
          rules={[{ required: true, message: "이메일을 입력하세요!" }]}
          validateStatus={emailError ? "error" : ""}
          help={emailError || undefined}
        >
          <Space size={0}>
            <Input
              value={id}
              onChange={(e) => { setId(e.target.value); setEmailError(""); }}
              addonAfter="@"
              placeholder="이메일"
            />
            {selectedOption !== "selfSelect" && (
              <Select
                defaultValue={defaultSelect}
                style={{ width: 120, height: "32.19px" }}
                onChange={(e) => setSelectedOption(e)}
                options={[
                  { value: "naver.com", label: "naver.com" },
                  { value: "daum.net", label: "daum.net" },
                  { value: "gmail.com", label: "gmail.com" },
                  { value: "selfSelect", label: "직접선택" },
                ]}
              />
            )}
            {selectedOption === "selfSelect" && (
              <Input
                value={selfSelect}
                onChange={(e) => setSelfSelect(e.target.value)}
                onBlur={() => { if (!selfSelect) setSelectedOption(defaultSelect); }}
                style={{ width: 120, height: "32.19px" }}
                placeholder="직접 입력"
              />
            )}
          </Space>
        </Form.Item>

        <Form.Item name="nickname" rules={[{ validator: validateNickName }]}>
          <Input placeholder="닉네임 (한/영/숫자 2~20자)" allowClear />
        </Form.Item>

        <Form.Item name="password" rules={[{ validator: validatePwd }]}>
          <Input.Password
            ref={passwordRef}
            placeholder="비밀번호 (영문+숫자+특수문자 8~16자)"
            allowClear
            iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
          />
        </Form.Item>

        <Form.Item name="passwordConfirm" rules={[{ validator: validatePwdConfirm }]}>
          <Input.Password
            placeholder="비밀번호 확인"
            allowClear
            iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
          />
        </Form.Item>

        <Form.Item>
          <Button
            className="button-type-round button-color-reverse"
            htmlType="submit"
            style={{ width: "100%", fontSize: 20, height: 48, border: "1px solid #f8f9fb" }}
          >
            인증 메일 받기
          </Button>
        </Form.Item>
      </Card>
    </AuthLayout>
  );
}
